import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatTile from "../components/StatTile";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Pencil, X, Plus, Save, ChevronLeft, AlertTriangle, PauseCircle, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { STATUS_OPTIONS } from "../lib/api";

const STATUS_LABEL = STATUS_OPTIONS.reduce((acc, s) => { acc[s.value] = s.label; return acc; }, {});
const STATUS_COLOR = STATUS_OPTIONS.reduce((acc, s) => { acc[s.value] = s.color; return acc; }, {});

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [customRows, setCustomRows] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  const isAdmin = user?.role === "admin";
  const isSmm = user?.role === "smm";
  const canSetAds = isAdmin || isSmm;

  const ADS_OPTS = [
    { value: "good", label: "Good", color: "#10B981" },
    { value: "average", label: "Average", color: "#F59E0B" },
    { value: "bad", label: "Bad", color: "#EF4444" },
    { value: "on_hold", label: "On Hold", color: "#737373" },
  ];

  const load = async () => {
    try {
      const r = await api.get(`/clients/${id}/dashboard`, { params: { month } });
      setData(r.data);
      setForm(r.data.client);
      setCustomRows(Object.entries(r.data.client.custom_fields || {}).map(([k, v]) => ({ key: k, value: v })));
    } catch (e) {
      toast.error("Failed to load");
    }
  };

  useEffect(() => { load(); }, [id, month]);

  const save = async () => {
    const custom_fields = {};
    customRows.forEach((r) => { if (r.key) custom_fields[r.key] = r.value; });
    try {
      await api.patch(`/clients/${id}`, { ...form, custom_fields });
      setEditing(false);
      load();
      toast.success("Saved");
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const toggleHold = async () => {
    try {
      const next = !data.client.on_hold;
      await api.patch(`/clients/${id}/hold`, { on_hold: next });
      toast.success(next ? "Client placed on HOLD" : "Hold removed");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const setAdsStatus = async (value) => {
    try {
      await api.patch(`/clients/${id}/ads-status`, { ads_status: value });
      toast.success("Ads status updated");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  if (!data) {
    return <div className="p-10 text-neutral-500 text-sm uppercase tracking-[0.3em]">Loading…</div>;
  }

  const { client, metrics, recent_tasks, recent_shoots, monthly } = data;

  return (
    <div data-testid="client-detail-page">
      <div className="px-6 lg:px-10 pt-6">
        <Link to="/clients" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-white transition-colors">
          <ChevronLeft className="w-3 h-3" /> All Clients
        </Link>
      </div>
      <PageHeader
        eyebrow={client.on_hold ? "⏸  ON HOLD · Client Dashboard" : "Client Dashboard"}
        title={client.name}
        description={client.contact}
        actions={
          isAdmin && (
            <div className="flex gap-2">
              <Button onClick={toggleHold} data-testid="hold-toggle-btn" className={`rounded-none border ${client.on_hold ? "bg-red-950 border-red-700 hover:bg-red-900 text-red-200" : "bg-neutral-900 border-neutral-800 hover:bg-neutral-800 text-white"}`}>
                {client.on_hold ? <PlayCircle className="w-4 h-4 mr-2" strokeWidth={1.5} /> : <PauseCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />}
                <span className="uppercase tracking-[0.15em] text-xs">{client.on_hold ? "Resume" : "Hold"}</span>
              </Button>
              {!editing ? (
                <Button onClick={() => setEditing(true)} data-testid="edit-client-btn" className="rounded-none bg-neutral-900 border border-neutral-800 hover:bg-neutral-800 text-white">
                  <Pencil className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  <span className="uppercase tracking-[0.15em] text-xs">Edit</span>
                </Button>
              ) : (
                <Button onClick={save} data-testid="save-client-btn" className="rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black">
                  <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  <span className="uppercase tracking-[0.15em] text-xs">Save</span>
                </Button>
              )}
            </div>
          )
        }
      />

      <div className="px-6 lg:px-10 py-8 space-y-10">
        {client.on_hold && (
          <div className="border border-red-700 bg-red-950/40 px-6 py-4 flex items-center gap-3" data-testid="hold-banner">
            <PauseCircle className="w-5 h-5 text-red-400" strokeWidth={1.5} />
            <div>
              <div className="text-sm font-medium text-red-200">Operations paused for this client.</div>
              <div className="text-xs text-red-300/70 mt-0.5">All teams should hold work until status is changed.</div>
            </div>
          </div>
        )}

        {monthly?.reminder && (
          <div className="border border-amber-700 bg-amber-950/40 px-6 py-4 flex items-center gap-3" data-testid="backlog-reminder">
            <AlertTriangle className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
            <div className="text-sm text-amber-100">{monthly.reminder}</div>
          </div>
        )}

        {/* Month picker */}
        <div className="flex items-center gap-3">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Month</div>
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="client-month-filter"
            className="w-44 h-8 rounded-none bg-neutral-900 border-neutral-800 text-xs"
          />
        </div>

        {/* Ads status (SMM/Admin) */}
        <div className="border border-neutral-900 px-5 py-4 flex flex-wrap items-center gap-3">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Ads Status</div>
          {ADS_OPTS.map((o) => {
            const active = client.ads_status === o.value;
            return (
              <button
                key={o.value}
                onClick={() => canSetAds && setAdsStatus(o.value)}
                disabled={!canSetAds}
                data-testid={`ads-${o.value}`}
                className="px-3 py-1 text-[10px] uppercase tracking-[0.2em] border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={
                  active
                    ? { borderColor: o.color, color: o.color, backgroundColor: "#171717" }
                    : { borderColor: "#262626", color: "#737373" }
                }
              >
                {o.label}
              </button>
            );
          })}
          {!client.ads_status && (
            <span className="text-[10px] text-neutral-600 italic">Not set — {canSetAds ? "pick one" : "ask SMM/Admin"}</span>
          )}
        </div>

        {/* Monthly stats */}
        {monthly && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-900 border border-neutral-900">
            <StatTile label="Shoots This Month" value={monthly.shoots_done_month} testId="month-shoots-done" sub={`Target: ${monthly.target}`} />
            <StatTile label="Remaining Shoots" value={monthly.remaining_month} accent="#F59E0B" testId="month-shoots-remaining" />
            <StatTile label="Edited / Posted" value={monthly.videos_edited_month} accent="#00C97A" testId="month-edited" />
            <StatTile label="Edit Backlog" value={monthly.backlog} accent={monthly.backlog >= 2 ? "#EF4444" : undefined} testId="month-backlog" />
          </div>
        )}

        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 -mb-4">All-time</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-900 border border-neutral-900">
          <StatTile label="Shots Done" value={metrics.shots_done} testId="metric-shots-done" />
          <StatTile label="Shots Pending" value={metrics.shots_pending} accent="#F59E0B" testId="metric-shots-pending" />
          <StatTile label="Videos Uploaded" value={metrics.videos_uploaded} sub={`${metrics.videos_published} published`} testId="metric-videos-uploaded" />
          <StatTile label="Videos Published" value={metrics.videos_published} accent="#10B981" testId="metric-videos-published" />
          <StatTile label="Amount Paid" value={`₹ ${Number(metrics.amount_paid).toLocaleString()}`} testId="metric-amount-paid" />
          <StatTile label="Amount Due" value={`₹ ${Number(metrics.amount_due).toLocaleString()}`} accent="#F59E0B" testId="metric-amount-due" />
          <StatTile label="Total Shoots" value={metrics.total_shoots} testId="metric-total-shoots" />
          <StatTile label="Shoot Revenue" value={`₹ ${Number(metrics.total_shoot_amount).toLocaleString()}`} testId="metric-shoot-revenue" />
        </div>

        {/* Edit form */}
        {editing && form && (
          <div className="border border-neutral-900 p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">Edit Client</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Field label="Contact" value={form.contact} onChange={(v) => setForm({ ...form, contact: v })} />
              <Field label="Shots Done" type="number" value={form.shots_done} onChange={(v) => setForm({ ...form, shots_done: Number(v) })} />
              <Field label="Shots Pending" type="number" value={form.shots_pending} onChange={(v) => setForm({ ...form, shots_pending: Number(v) })} />
              <Field label="Amount Paid" type="number" value={form.amount_paid} onChange={(v) => setForm({ ...form, amount_paid: Number(v) })} />
              <Field label="Amount Due" type="number" value={form.amount_due} onChange={(v) => setForm({ ...form, amount_due: Number(v) })} />
              <Field label="Monthly Shoot Target" type="number" value={form.monthly_shoot_target} onChange={(v) => setForm({ ...form, monthly_shoot_target: Number(v) })} />
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Custom Fields</Label>
                  <Button size="sm" variant="ghost" onClick={() => setCustomRows((p) => [...p, { key: "", value: "" }])} className="h-7 rounded-none text-xs uppercase tracking-wider hover:bg-neutral-900">
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {customRows.map((r, i) => (
                    <div key={i} className="grid grid-cols-[1fr,1fr,auto] gap-2">
                      <Input placeholder="Field name" value={r.key} onChange={(e) => setCustomRows((p) => p.map((row, idx) => idx === i ? { ...row, key: e.target.value } : row))} className="rounded-none bg-neutral-900 border-neutral-800" />
                      <Input placeholder="Value" value={r.value} onChange={(e) => setCustomRows((p) => p.map((row, idx) => idx === i ? { ...row, value: e.target.value } : row))} className="rounded-none bg-neutral-900 border-neutral-800" />
                      <Button size="icon" variant="ghost" onClick={() => setCustomRows((p) => p.filter((_, idx) => idx !== i))} className="rounded-none hover:bg-neutral-900">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Custom fields display */}
        {!editing && Object.keys(client.custom_fields || {}).length > 0 && (
          <div className="border border-neutral-900 p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">Custom Fields</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(client.custom_fields).map(([k, v]) => (
                <div key={k} className="border border-neutral-900 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{k}</div>
                  <div className="text-sm mt-1">{String(v)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent tasks */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-neutral-900">
            <div className="px-5 py-4 border-b border-neutral-900 text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              Recent Tasks
            </div>
            <div className="divide-y divide-neutral-900">
              {recent_tasks.length === 0 && <div className="px-5 py-6 text-sm text-neutral-500">No tasks yet.</div>}
              {recent_tasks.map((t) => (
                <div key={t.task_id} className="px-5 py-4 flex items-center justify-between" data-testid={`client-task-${t.task_id}`}>
                  <div className="text-sm truncate">{t.title}</div>
                  <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border rounded-full" style={{ color: STATUS_COLOR[t.status], borderColor: STATUS_COLOR[t.status] }}>
                    {STATUS_LABEL[t.status]}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-neutral-900">
            <div className="px-5 py-4 border-b border-neutral-900 text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              Recent Shoots
            </div>
            <div className="divide-y divide-neutral-900">
              {recent_shoots.length === 0 && <div className="px-5 py-6 text-sm text-neutral-500">No shoots logged.</div>}
              {recent_shoots.map((s) => (
                <div key={s.shoot_id} className="px-5 py-4 flex items-center justify-between" data-testid={`client-shoot-${s.shoot_id}`}>
                  <div>
                    <div className="text-sm">{s.title || "Shoot"}</div>
                    <div className="text-xs text-neutral-500 font-mono mt-0.5">{s.date}</div>
                  </div>
                  <div className="text-sm font-mono">₹ {Number(s.amount).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">{label}</Label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" />
    </div>
  );
}
